import { ReactNode, useEffect, useState } from "react";
import CoreLayout from "@layouts/core/components/CoreLayout";
import LoginFlowWrapper from "@common/components/LoginFlowWrapper";
import { LoginModalMode, loginMode } from "@modules/user/types/LoginModalMode";
import useUser from "@modules/user/hooks/useUser";
import useHappyFox from "@modules/happyfox/useHappyFox";
import ResumeFooter from "@common/components/ResumeFooter";
import useGetPurchasesByDealIds from "@modules/purchase/hooks/useGetPurchasesByDealIds";
import getUserDealIdsByType from "@modules/purchase/helpers/getUserDealIdsByType";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import { useRouter } from "next/router";
import ResumePurchaseModal from "@modules/purchase/components/ResumePurchaseModal";
import DealStatusEnum from "@common/enums/DealStatusEnum";
import NavBar from "./NavBar";
import Footer from "./Footer";

type Props = {
  children: ReactNode;
  onLogin?: () => void;
};

const PublicLayout = ({ children, onLogin }: Props) => {
  const [isNotInIframe, setIsInIframe] = useState<boolean | null>(null);

  useEffect(() => {
    setIsInIframe(window === window.top);
  }, [isNotInIframe]);

  useHappyFox({ isEnabled: isNotInIframe ?? false });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user } = useUser();

  const [loginModalMode, setLoginModalMode] =
    useState<LoginModalMode>(loginMode);

  const dealIds = getUserDealIdsByType(user, DealTypeEnum.BuyIn);

  const { activePurchases } = useGetPurchasesByDealIds(dealIds);

  const nonPurchasedPurchases = activePurchases.filter(
    (p) => p.dealStatusId !== DealStatusEnum.Purchased
  );

  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);

  const { push, query } = useRouter();

  const handleResumeClick = () => {
    if (nonPurchasedPurchases.length === 1) {
      push(
        {
          pathname: "/confirm",
          query: { ...query, dealId: nonPurchasedPurchases[0].id },
        },
        undefined,
        { shallow: true }
      );
    } else {
      setIsResumeModalOpen(true);
    }
  };

  return (
    <>
      <CoreLayout>
        <LoginFlowWrapper
          isLoginModalOpen={showLoginModal}
          onLoginModalClose={() => setShowLoginModal(false)}
          onLogin={onLogin}
          loginModalMode={loginModalMode}
        >
          <NavBar
            user={user}
            setShowLoginModal={setShowLoginModal}
            setLoginModalMode={setLoginModalMode}
          />
          {children}
          <Footer />
        </LoginFlowWrapper>

        {nonPurchasedPurchases.length > 0 && (
          <ResumeFooter
            dealCount={nonPurchasedPurchases.length}
            onResumeClick={handleResumeClick}
          />
        )}
      </CoreLayout>
      <ResumePurchaseModal
        isOpen={isResumeModalOpen}
        onClose={() => setIsResumeModalOpen(false)}
        purchases={nonPurchasedPurchases}
      />
    </>
  );
};

export default PublicLayout;
